import { useEffect, useState, useCallback, useRef } from "react";
import { User, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import {
  isEmailEnumerationRisk,
  isObfuscatedExistingSignupUser,
  maskAuthSensitiveError,
} from "@/lib/auth-messages";
import { getGlobalSingleton } from "@/lib/global-singleton";
import { ensureFreshSession, shouldClearLocalSession } from "@/lib/auth-refresh";
import { queryClient } from "@/lib/query-client";

// ─── Singleton Auth Store (globalThis — sobrevive ao HMR do Vite) ───

type AuthListener = () => void;

interface AuthStoreState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  initialized: boolean;
  sessionValidationEpoch: number;
  listeners: Set<AuthListener>;
  subscriptionCleanup: (() => void) | null;
  initDone: boolean;
}

function authStore(): AuthStoreState {
  return getGlobalSingleton<AuthStoreState>("__fabchatAuthStore__", () => ({
    user: null,
    session: null,
    loading: true,
    initialized: false,
    sessionValidationEpoch: 0,
    listeners: new Set<AuthListener>(),
    subscriptionCleanup: null,
    initDone: false,
  }));
}

// Refresh disabled — no automatic token refresh to reduce DB pressure
let refreshFailures = 0;

function notifyListeners() {
  authStore().listeners.forEach((fn) => fn());
}

function clearStoredAuthSession() {
  try {
    const shouldRemove = (key: string) =>
      (key.startsWith("sb-") && key.includes("auth-token")) ||
      key === "supabase.auth.token";

    Object.keys(localStorage)
      .filter(shouldRemove)
      .forEach((key) => localStorage.removeItem(key));

    Object.keys(sessionStorage)
      .filter(shouldRemove)
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

function resetAuthStore() {
  const state = authStore();
  state.user = null;
  state.session = null;
  state.loading = false;
  state.initialized = true;
  clearStoredAuthSession();
  notifyListeners();
}

function applyAuthSession(session: Session | null) {
  const state = authStore();
  state.sessionValidationEpoch += 1;
  state.session = session;
  state.user = session?.user ?? null;
  state.loading = false;
  state.initialized = true;
  notifyListeners();
}

function initStore() {
  const state = authStore();
  if (state.initDone) return;
  state.initDone = true;

  if (!state.subscriptionCleanup) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event: AuthChangeEvent, newSession: Session | null) => {
        if (_event === "SIGNED_IN" || _event === "TOKEN_REFRESHED") {
          state.sessionValidationEpoch += 1;
        }

        // Propaga o token novo para o socket de realtime já aberto.
        // Sem isso o canal fica "conectado" mas para de entregar linhas após a expiração do JWT.
        if (_event === "SIGNED_IN" || _event === "TOKEN_REFRESHED" || _event === "SIGNED_OUT") {
          try {
            supabase.realtime.setAuth(newSession?.access_token ?? null);
          } catch (err) {
            console.warn("[Auth] realtime.setAuth falhou:", err);
          }
        }



        state.session = newSession;
        state.user = newSession?.user ?? null;

        if (state.loading) {
          state.loading = false;
        }

        if (_event === "SIGNED_OUT") {
          state.user = null;
          state.session = null;
          state.initialized = false;
        }

        if (_event === "TOKEN_REFRESHED" || _event === "SIGNED_IN") {
          refreshFailures = 0;
        }

        notifyListeners();
      }
    );

    state.subscriptionCleanup = () => subscription.unsubscribe();
  }

  // Já hidratado (ex.: HMR) — não revalidar e não zerar sessão.
  if (state.session && state.user) {
    state.loading = false;
    notifyListeners();
    return;
  }

  const validationEpoch = ++state.sessionValidationEpoch;
  const SESSION_INIT_TIMEOUT_MS = 12000;
  let sessionInitDone = false;

  const finishSessionInit = (s: Session | null) => {
    if (sessionInitDone || validationEpoch !== state.sessionValidationEpoch) return;
    sessionInitDone = true;
    state.session = s;
    state.user = s?.user ?? null;
    state.loading = false;
    state.initialized = true;
    notifyListeners();
  };

  const sessionInitTimer = setTimeout(() => {
    if (!sessionInitDone && state.loading) {
      console.warn("[Auth] getSession timed out — continuando sem sessão");
      finishSessionInit(null);
    }
  }, SESSION_INIT_TIMEOUT_MS);

  supabase.auth.getSession().then(async ({ data: { session: s }, error }) => {
    if (validationEpoch !== state.sessionValidationEpoch) return;

    if (error) {
      console.warn("[Auth] Initial session fetch failed:", error.message);
      refreshFailures++;
    }

    if (s) {
      const validatedToken = s.access_token;
      if (!import.meta.env.DEV) {
        const { error: userError } = await supabase.auth.getUser();
        if (validationEpoch !== state.sessionValidationEpoch) return;
        if (state.session && state.session.access_token !== validatedToken) return;

        if (userError) {
          // Só descarta a sessão quando a credencial é realmente inválida.
          // 429/rede/timeout são transitórios (rajada de renovações) e não devem deslogar.
          if (shouldClearLocalSession(userError)) {
            console.warn("[Auth] Stored session is invalid, clearing local auth:", userError.message);
            clearTimeout(sessionInitTimer);
            sessionInitDone = true;
            resetAuthStore();
            return;
          }
          console.warn("[Auth] Validação transitória falhou, mantendo sessão:", userError.message);
        }
      }
    }

    if (!s && state.session) {
      clearTimeout(sessionInitTimer);
      return;
    }

    clearTimeout(sessionInitTimer);
    finishSessionInit(s);
  });
}

initStore();

// ─── Hook ───────────────────────────────────────────────────────────

export const useAuth = () => {
  const [, forceUpdate] = useState(0);
  const navigate = useNavigate();
  const state = authStore();
  const currentUser = state.user;
  const currentSession = state.session;
  const currentLoading = state.loading;
  const prevUserRef = useRef(state.user?.id);
  const prevSignedOutRef = useRef(false);
  const authRedirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const listener = () => {
      forceUpdate((c) => c + 1);
    };
    state.listeners.add(listener);
    return () => {
      state.listeners.delete(listener);
    };
  }, [state]);

  useEffect(() => {
    if (authRedirectTimerRef.current) {
      clearTimeout(authRedirectTimerRef.current);
      authRedirectTimerRef.current = null;
    }

    if (!currentLoading && prevUserRef.current && !currentUser && !prevSignedOutRef.current) {
      // Janela de tolerância maior para mobile/PWA (iOS suspende JS no background;
      // ao voltar, getSession() pode demorar 2-5s para reidratar do localStorage).
      // Só redireciona se realmente não houver sessão após múltiplas tentativas.
      const TOLERANCE_MS = 5000;
      authRedirectTimerRef.current = setTimeout(async () => {
        // Tenta até 3x com pequenos intervalos antes de desistir.
        for (let i = 0; i < 3; i++) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            state.session = session;
            state.user = session.user;
            state.initialized = true;
            prevSignedOutRef.current = false;
            notifyListeners();
            return;
          }
          if (i < 2) await new Promise((r) => setTimeout(r, 800));
        }

        // Se a aba está oculta (telefone bloqueado), não redireciona — espera o foco voltar.
        if (typeof document !== "undefined" && document.visibilityState === "hidden") {
          return;
        }

        prevSignedOutRef.current = true;
        navigate("/auth");
      }, TOLERANCE_MS);
    }
    if (currentUser) {
      prevSignedOutRef.current = false;
    }
    prevUserRef.current = currentUser?.id;

    return () => {
      if (authRedirectTimerRef.current) {
        clearTimeout(authRedirectTimerRef.current);
        authRedirectTimerRef.current = null;
      }
    };
  }, [currentUser, currentLoading, navigate]);

  // Quando o app volta do background (PWA mobile), revalida a sessão com Supabase
  // para forçar refresh do token antes que listeners reajam a um estado vazio temporário.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        // Renovação coordenada (trava entre abas + intervalo mínimo) para não gerar rajadas.
        const session = await ensureFreshSession();
        if (session && (!state.session || state.session.access_token !== session.access_token)) {
          state.session = session;
          state.user = session.user;
          state.initialized = true;
          notifyListeners();
        }
      } catch {
        /* noop */
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/tasks`,
      },
    });

    const signupEmailUnavailable =
      isObfuscatedExistingSignupUser(data) ||
      (error?.message ? isEmailEnumerationRisk(error.message) : false);

    return {
      error: maskAuthSensitiveError(error),
      data,
      signupEmailUnavailable,
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({ email, password });
    if (!error && data.session) {
      refreshFailures = 0;
      // Atualiza o store de forma síncrona antes do navigate para evitar tela preta
      // enquanto onAuthStateChange ainda não disparou.
      applyAuthSession(data.session);
      // Let Auth.tsx's `next` handler decide when a deep-link redirect is pending.
      const pendingNext = new URLSearchParams(window.location.search).get("next");
      if (!pendingNext || !pendingNext.startsWith("/") || pendingNext.startsWith("//")) {
        navigate("/tasks");
      }
    }
    return { error, data };
  }, [navigate]);

  const signInWithGoogle = useCallback(async () => {
    // Preserve `?next=` so MCP OAuth consent (and other deep links) survive the Google round-trip.
    const currentNext = new URLSearchParams(window.location.search).get("next");
    const redirectTo =
      currentNext && currentNext.startsWith("/") && !currentNext.startsWith("//")
        ? `${window.location.origin}/auth?next=${encodeURIComponent(currentNext)}`
        : `${window.location.origin}/auth`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    // Use scope 'local' so logout always succeeds even when the refresh token
    // is missing/expired (otherwise Supabase tries to revoke the token on the
    // server and returns an error, leaving the user "stuck" logged in).
    let error: any = null;
    try {
      const res = await supabase.auth.signOut({ scope: "local" });
      error = res.error;
    } catch (e) {
      error = e;
    }
    resetAuthStore();
    queryClient.clear();
    navigate("/auth", { replace: true });
    return { error };
  }, [navigate]);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: maskAuthSensitiveError(error) };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  }, []);

  const resendConfirmationEmail = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resend({ type: "signup", email });
    return { error: maskAuthSensitiveError(error) };
  }, []);

  const linkGoogleAccount = useCallback(async () => {
    const { data, error } = await supabase.auth.linkIdentity({ provider: "google" });
    return { data, error };
  }, []);

  const updateEmail = useCallback(async (newEmail: string) => {
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    return { error: maskAuthSensitiveError(error) };
  }, []);

  return {
    user: currentUser,
    session: currentSession,
    loading: currentLoading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    resendConfirmationEmail,
    linkGoogleAccount,
    updateEmail,
  };
};
